import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor from "@/components/RichTextEditor";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, X, Save, Pencil, FileText, Tag, TrendingUp, Wallet, ClipboardList, Activity, FileDown } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";
import { fmtDateTime } from "@/lib/dates";

/**
 * ClientBusinessTab — the per-client "playbook" view.
 *
 * Five fixed sections:
 *   1. Business Overview (rich text)
 *   2. Key Operations (rich text)
 *   3. Revenue Heads (list of {name, notes})
 *   4. Expense Heads & Vendors (list of {name, vendors[], notes})
 *   5. Our Responsibilities (rich text)
 *   6. Current Status / What's Going On (rich text)
 *
 * Edits are gated behind `canEdit`. Each section saves independently to
 * minimise the blast radius of a slow save and keep the perceived speed high.
 */
export default function ClientBusinessTab({ clientId, canEdit }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/clients/${clientId}/business-profile`);
      setProfile(r.data);
    } catch (e) {
      toast.error(parseApiError(e, "Failed to load business profile"));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (fields) => {
    try {
      const r = await api.patch(`/clients/${clientId}/business-profile`, fields);
      setProfile(r.data);
      toast.success("Saved");
      return r.data;
    } catch (e) {
      toast.error(parseApiError(e, "Save failed"));
      throw e;
    }
  };

  if (loading) {
    return <div className="text-xs text-slate-400 py-10 text-center">Loading business profile…</div>;
  }
  if (!profile) return null;

  const exportPdf = async () => {
    try {
      const resp = await api.get(`/clients/${clientId}/business-profile/export-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      // Filename comes back via Content-Disposition; browser handles it for blob anchors.
      a.download = "business-profile.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(parseApiError(e, "PDF export failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="business-tab">
      <div className="flex items-center justify-end">
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm"
          onClick={exportPdf}
          data-testid="btn-export-business-pdf"
        >
          <FileDown size={12} className="mr-1.5" /> Export PDF
        </Button>
      </div>
      <RichSection
        testId="section-overview"
        icon={FileText}
        title="Business Overview"
        hint="How the business functions — products, services, customer profile, geography."
        value={profile.overview || ""}
        canEdit={canEdit}
        onSave={(v) => patch({ overview: v })}
      />

      <RichSection
        testId="section-operations"
        icon={Activity}
        title="Key Operations"
        hint="Day-to-day workflows, key partnerships, where the work happens."
        value={profile.operations || ""}
        canEdit={canEdit}
        onSave={(v) => patch({ operations: v })}
      />

      <RevenueHeadsSection
        heads={profile.revenue_heads || []}
        canEdit={canEdit}
        onSave={(heads) => patch({ revenue_heads: heads })}
      />

      <ExpenseHeadsSection
        heads={profile.expense_heads || []}
        canEdit={canEdit}
        onSave={(heads) => patch({ expense_heads: heads })}
      />

      <RichSection
        testId="section-responsibilities"
        icon={ClipboardList}
        title="Our Responsibilities"
        hint="What this firm delivers for the client — filings owned, deliverables, cadence."
        value={profile.responsibilities || ""}
        canEdit={canEdit}
        onSave={(v) => patch({ responsibilities: v })}
      />

      <RichSection
        testId="section-current-status"
        icon={Tag}
        title="Current Status / What's Going On"
        hint="Live notes — recent events, blocking issues, anything an exec should know."
        value={profile.current_status || ""}
        canEdit={canEdit}
        onSave={(v) => patch({ current_status: v })}
      />

      {profile.updated_at && (
        <p className="text-[10px] text-slate-400 font-mono-data text-right" data-testid="bp-updated-at">
          Last updated {fmtDateTime(profile.updated_at)}
        </p>
      )}
    </div>
  );
}

// --- Rich-text section card with edit / view modes ---
function RichSection({ testId, icon: Icon, title, hint, value, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value); }, [value]);

  const isEmpty = !(value || "").replace(/<[^>]*>/g, "").trim();

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch { /* toast handled by parent */ } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 border-slate-200 shadow-none rounded-sm" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0 mt-0.5 w-7 h-7 rounded-sm border border-slate-200 inline-flex items-center justify-center text-slate-500">
            <Icon size={14} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold tracking-tight text-[#0a2540]" style={{ fontFamily: "Outfit" }}>{title}</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>
          </div>
        </div>
        {canEdit && !editing && (
          <Button size="sm" variant="outline" className="rounded-sm shrink-0" onClick={() => { setDraft(value); setEditing(true); }} data-testid={`${testId}-edit`}>
            <Pencil size={12} className="mr-1.5" /> {isEmpty ? "Add" : "Edit"}
          </Button>
        )}
      </div>

      <div className="mt-4">
        {editing ? (
          <>
            <RichTextEditor value={draft} onChange={setDraft} maxHeight="40vh" />
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditing(false)} data-testid={`${testId}-cancel`}>Cancel</Button>
              <Button size="sm" className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={save} disabled={saving} data-testid={`${testId}-save`}>
                <Save size={12} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </>
        ) : isEmpty ? (
          <div className="text-xs text-slate-400 italic">{canEdit ? "Click Add to start filling this section." : "Nothing recorded yet."}</div>
        ) : (
          // Sanitisation already happens upstream inside RichTextEditor.
          // Re-render the saved HTML in the same .prose styles for visual parity.
          // eslint-disable-next-line react/no-danger
          <div className="prose prose-sm max-w-none text-[#0a2540]" dangerouslySetInnerHTML={{ __html: value }} />
        )}
      </div>
    </Card>
  );
}

// --- Revenue Heads ---
function RevenueHeadsSection({ heads, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(heads);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(heads); }, [heads]);

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = draft
        .map((h) => ({ name: (h.name || "").trim(), notes: (h.notes || "").trim() }))
        .filter((h) => h.name);
      await onSave(cleaned);
      setEditing(false);
    } catch { /* toast handled */ } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 border-slate-200 shadow-none rounded-sm" data-testid="section-revenue-heads">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0 mt-0.5 w-7 h-7 rounded-sm border border-slate-200 inline-flex items-center justify-center text-emerald-600">
            <TrendingUp size={14} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold tracking-tight text-[#0a2540]" style={{ fontFamily: "Outfit" }}>Revenue Heads</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">Major sources of revenue. A guide for accountants tagging entries.</p>
          </div>
        </div>
        {canEdit && !editing && (
          <Button size="sm" variant="outline" className="rounded-sm shrink-0" onClick={() => { setDraft(heads); setEditing(true); }} data-testid="rh-edit">
            <Pencil size={12} className="mr-1.5" /> {heads.length === 0 ? "Add" : "Edit"}
          </Button>
        )}
      </div>

      <div className="mt-4">
        {editing ? (
          <>
            <div className="space-y-2" data-testid="rh-editor">
              {draft.map((h, i) => (
                <div key={i} className="flex items-start gap-2 border border-slate-200 rounded-sm p-2">
                  <div className="flex-1 space-y-1.5">
                    <Input
                      value={h.name}
                      onChange={(e) => setDraft(draft.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                      placeholder="e.g. Product sales, Consulting fees"
                      className="rounded-sm text-sm"
                      data-testid={`rh-name-${i}`}
                    />
                    <Textarea
                      value={h.notes || ""}
                      onChange={(e) => setDraft(draft.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))}
                      placeholder="Optional — tagging hints, common cases, exceptions."
                      className="rounded-sm text-xs min-h-0 h-16 resize-none"
                      data-testid={`rh-notes-${i}`}
                    />
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-600 h-8 w-8 p-0 shrink-0" onClick={() => setDraft(draft.filter((_, j) => j !== i))} data-testid={`rh-del-${i}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="rounded-sm w-full" onClick={() => setDraft([...draft, { name: "", notes: "" }])} data-testid="rh-add">
                <Plus size={12} className="mr-1.5" /> Add revenue head
              </Button>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditing(false)} data-testid="rh-cancel">Cancel</Button>
              <Button size="sm" className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={save} disabled={saving} data-testid="rh-save">
                <Save size={12} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </>
        ) : heads.length === 0 ? (
          <div className="text-xs text-slate-400 italic">No revenue heads recorded yet.</div>
        ) : (
          <ul className="space-y-2">
            {heads.map((h, i) => (
              <li key={i} className="flex items-start gap-3 px-3 py-2 border border-slate-100 rounded-sm bg-slate-50/50" data-testid={`rh-row-${i}`}>
                <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-sm bg-emerald-50 text-emerald-700 font-mono-data text-[10px] font-semibold">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#0a2540]">{h.name}</div>
                  {h.notes && <div className="text-[11px] text-slate-500 mt-0.5 whitespace-pre-wrap">{h.notes}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

// --- Expense Heads & Vendors ---
function ExpenseHeadsSection({ heads, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(heads);
  const [saving, setSaving] = useState(false);
  const [vendorDrafts, setVendorDrafts] = useState({}); // per-head temp input

  useEffect(() => { setDraft(heads); }, [heads]);

  const addVendor = (i) => {
    const v = (vendorDrafts[i] || "").trim();
    if (!v) return;
    if ((draft[i].vendors || []).some((x) => x.toLowerCase() === v.toLowerCase())) {
      setVendorDrafts((s) => ({ ...s, [i]: "" }));
      return;
    }
    setDraft(draft.map((x, j) => j === i ? { ...x, vendors: [...(x.vendors || []), v] } : x));
    setVendorDrafts((s) => ({ ...s, [i]: "" }));
  };

  const removeVendor = (i, vi) => {
    setDraft(draft.map((x, j) => j === i ? { ...x, vendors: x.vendors.filter((_, k) => k !== vi) } : x));
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = draft
        .map((h) => ({
          name: (h.name || "").trim(),
          vendors: (h.vendors || []).map((v) => v.trim()).filter(Boolean),
          notes: (h.notes || "").trim(),
        }))
        .filter((h) => h.name);
      await onSave(cleaned);
      setEditing(false);
    } catch { /* toast handled */ } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 border-slate-200 shadow-none rounded-sm" data-testid="section-expense-heads">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0 mt-0.5 w-7 h-7 rounded-sm border border-slate-200 inline-flex items-center justify-center text-amber-600">
            <Wallet size={14} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold tracking-tight text-[#0a2540]" style={{ fontFamily: "Outfit" }}>Expense Heads &amp; Vendors</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">Major expense categories. Add common vendors under each — accountants can use this to tag entries correctly.</p>
          </div>
        </div>
        {canEdit && !editing && (
          <Button size="sm" variant="outline" className="rounded-sm shrink-0" onClick={() => { setDraft(heads); setEditing(true); }} data-testid="eh-edit">
            <Pencil size={12} className="mr-1.5" /> {heads.length === 0 ? "Add" : "Edit"}
          </Button>
        )}
      </div>

      <div className="mt-4">
        {editing ? (
          <>
            <div className="space-y-3" data-testid="eh-editor">
              {draft.map((h, i) => (
                <div key={i} className="border border-slate-200 rounded-sm p-2.5 space-y-2">
                  <div className="flex items-start gap-2">
                    <Input
                      value={h.name}
                      onChange={(e) => setDraft(draft.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                      placeholder="e.g. Rent, Salaries, Logistics"
                      className="rounded-sm text-sm"
                      data-testid={`eh-name-${i}`}
                    />
                    <Button size="sm" variant="ghost" className="text-red-600 h-8 w-8 p-0 shrink-0" onClick={() => setDraft(draft.filter((_, j) => j !== i))} data-testid={`eh-del-${i}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Vendors</div>
                    <div className="flex flex-wrap gap-1.5 mb-2 min-h-[24px]">
                      {(h.vendors || []).length === 0 && (
                        <span className="text-[10px] text-slate-300 italic">No vendors yet</span>
                      )}
                      {(h.vendors || []).map((v, vi) => (
                        <span key={vi} className="inline-flex items-center gap-1 px-2 py-0.5 border border-slate-200 bg-white rounded-sm text-[11px] text-slate-700" data-testid={`eh-vendor-${i}-${vi}`}>
                          {v}
                          <button onClick={() => removeVendor(i, vi)} className="text-slate-400 hover:text-red-600" aria-label="Remove vendor">
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={vendorDrafts[i] || ""}
                        onChange={(e) => setVendorDrafts((s) => ({ ...s, [i]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            addVendor(i);
                          }
                        }}
                        placeholder="Add vendor name, press Enter"
                        className="rounded-sm text-xs h-8"
                        data-testid={`eh-vendor-input-${i}`}
                      />
                      <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => addVendor(i)} data-testid={`eh-vendor-add-${i}`}>
                        Add
                      </Button>
                    </div>
                  </div>

                  <Textarea
                    value={h.notes || ""}
                    onChange={(e) => setDraft(draft.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))}
                    placeholder="Optional — tagging hints, exceptions, sub-categories."
                    className="rounded-sm text-xs min-h-0 h-16 resize-none"
                    data-testid={`eh-notes-${i}`}
                  />
                </div>
              ))}
              <Button size="sm" variant="outline" className="rounded-sm w-full" onClick={() => setDraft([...draft, { name: "", vendors: [], notes: "" }])} data-testid="eh-add">
                <Plus size={12} className="mr-1.5" /> Add expense head
              </Button>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditing(false)} data-testid="eh-cancel">Cancel</Button>
              <Button size="sm" className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={save} disabled={saving} data-testid="eh-save">
                <Save size={12} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </>
        ) : heads.length === 0 ? (
          <div className="text-xs text-slate-400 italic">No expense heads recorded yet.</div>
        ) : (
          <ul className="space-y-2">
            {heads.map((h, i) => (
              <li key={i} className="px-3 py-2 border border-slate-100 rounded-sm bg-slate-50/50" data-testid={`eh-row-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-sm bg-amber-50 text-amber-700 font-mono-data text-[10px] font-semibold">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#0a2540]">{h.name}</div>
                    {(h.vendors || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {h.vendors.map((v, vi) => (
                          <span key={vi} className="inline-flex items-center px-2 py-0.5 border border-amber-200 bg-amber-50/60 rounded-sm text-[10px] text-amber-800 font-mono-data">
                            {v}
                          </span>
                        ))}
                      </div>
                    )}
                    {h.notes && <div className="text-[11px] text-slate-500 mt-1.5 whitespace-pre-wrap">{h.notes}</div>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
